import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/analysis/analysis.response.type';
import * as Request from '@shared/models/analysis/analysis.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setReportList, setSingleReport, setSkillStatistics, setFormValues } from '../slices/report.slice';
import { setQuickAddProjects } from '../slices/project.slice';

export const AnalysisReportEndpoints = {
    getReportList: config.base_url + '/analysis/report',
    createReport: config.base_url + '/analysis/report',
    editReport: (reportId: string) => config.base_url + `/analysis/report/${reportId}`,
    getUserSkillsStatistics: (reportId: string) => config.base_url + `/analysis/report/${reportId}/review`,
    getSingleReport: (projectId: string) => config.base_url + `/analysis/report/${projectId}`,
    getReportReview: config.base_url + '/analysis/report/review',
    addReportProjects: (reportId: string) => config.base_url + `/analysis/report/${reportId}/project`,
    deleteReport: (reportId: string) => config.base_url + `/analysis/report/${reportId}`,
};

export const fetchAnalysisReportList = createAsyncThunk<void, Request.IFetchReportListQueryType>(
    AnalysisReportEndpoints.getReportList,
    async (query, { dispatch }) => {
        try {
            const params = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IFetchReportListType>>(
                `${AnalysisReportEndpoints.getReportList}?${params}`
            );

            dispatch(setReportList(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching reports of analysis, query: ${AnalysisReportEndpoints.getReportList}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const createReport = createAsyncThunk<void, Request.ICreateReportQueryType>(
    AnalysisReportEndpoints.createReport + '/create',
    async (query, { dispatch }) => {
        try {
            const { reportId } = query.params;
            const formData = new FormData();
            formData.append('reportName', query.params.reportName);
            formData.append('color', query.params.color);
            formData.append('firstName', query.params.firstName);
            formData.append('surname', query.params.surname);
            formData.append('birthDate', query.params.birthDate);
            formData.append('school', query.params.school);
            formData.append('email', query.params.email);
            formData.append('graduationDate', query.params.graduationDate);
            formData.append('vision', query.params.vision);
            if (typeof query.params.image !== 'string') {
                formData.append('image', query.params.image);
            }
            const res = await httpService[reportId ? 'patch' : 'post']<AscResponse<Response.ICreateReportResponseType>>(
                reportId ? AnalysisReportEndpoints.editReport(reportId) : AnalysisReportEndpoints.createReport,
                formData
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error ${query.params.reportId ? 'editing' : 'creating'} a report on analysis, query ${
                        query.params.reportId
                            ? AnalysisReportEndpoints.editReport(query.params.reportId)
                            : AnalysisReportEndpoints.createReport
                    }`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchUserSkillsStatistics = createAsyncThunk<void, Request.IFetchUserSkillsStatsQueryType>(
    AnalysisReportEndpoints.getUserSkillsStatistics('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.post<AscResponse<{ stats: Response.IFetchUserSkillsStatsResponse }>>(
                `${AnalysisReportEndpoints.getUserSkillsStatistics(query.params.reportId)}`
            );

            const getStats = (object, key: string) => {
                delete object[key];
                return object;
            };

            dispatch(
                setSkillStatistics({
                    baseline: res.data.data?.stats?.baseline,
                    stats: getStats(res.data.data.stats, 'baseline'),
                })
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching statistics of user skills on analysis, query ${AnalysisReportEndpoints.getUserSkillsStatistics(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSingleReport = createAsyncThunk<void, Request.IFetchSingleReportQueryType>(
    AnalysisReportEndpoints.getSingleReport('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchSingleReportType>>(
                AnalysisReportEndpoints.getSingleReport(query.params.reportId)
            );

            const data = res.data.data;

            dispatch(setSingleReport(data));

            dispatch(
                setFormValues({
                    reportName: data.reportName,
                    color: data.color,
                    firstName: data.firstName,
                    surname: data.surname,
                    birthDate: dayjs(data.birthDate).format('DD/MM/YYYY'),
                    school: data.school,
                    graduationDate: dayjs(data.graduationDate).format('DD/MM/YYYY'),
                    email: data.email,
                    vision: data.vision,
                    image: data.profilePicture.location,
                })
            );
            if (data.reportStats) {
                dispatch(setSkillStatistics({ baseline: data?.reportStats?.baseline, stats: data?.reportStats }));
            }
            if (data?.reportProjects?.length > 0) {
                dispatch(setQuickAddProjects(data?.reportProjects.map((el) => el?.id)));
            } else {
                dispatch(setQuickAddProjects([null]));
            }

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching single project of analysis, query: ${AnalysisReportEndpoints.getSingleReport('id')}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const addReportProjects = createAsyncThunk<void, Request.IAddReportProjectsQueryType>(
    AnalysisReportEndpoints.addReportProjects('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.post<AscResponse<{ isAdded: boolean }>>(
                AnalysisReportEndpoints.addReportProjects(query.params.reportId),
                { projects: query.params.projects }
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error adding projects to a report on analysis, query ${AnalysisReportEndpoints.addReportProjects(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const deleteReportQuery = createAsyncThunk<void, Request.IDeleteReportRequest>(
    AnalysisReportEndpoints.deleteReport('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.delete<AscResponse<{ isDeleted: boolean }>>(
                `${AnalysisReportEndpoints.deleteReport(query.params.reportId)}`
            );

            if (res.data) toast.success('The report has been successfully deleted');

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error deleting a report, query: ${AnalysisReportEndpoints.deleteReport('id')}`
                ),
                { type: 'error' }
            );
        }
    }
);
